import {  Component, OnDestroy, inject, input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { WorkBook, WorkSheet, read } from 'xlsx';
import { APIService } from 'src/app/services/APIService/api.service';

import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { PlotlyPlotComponent } from '../../plotly-plot/plotly-plot.component';
import { redraw, type PlotMouseEvent, type PlotlyHTMLElement } from 'plotly.js-basic-dist-min';
import { ConfirmService } from 'src/app/services/confirm.service';
import { LOWESS } from 'src/app/services/Shared/lowess';

type Point = {
  x: number;
  y: number;
};

@Component({
  selector: 'app-railmonitor',
  templateUrl: './railmonitor.component.html',
  styleUrls: ['./railmonitor.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    PlotlyPlotComponent,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    TablePrettyPrintPipe,
  ],
})
export class RailmonitorComponent implements  OnDestroy {
  private readonly apiservice = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  authService = inject(AuthService);
  private readonly toastr = inject(ToastrService);
  protected localeDate = inject(BackendLocaleDatePipe);
  private readonly confirmService = inject(ConfirmService);

  private stammid: number;
  public profiles: any = [];
  public sollProfile: any = {};
  public curSoll = '';
  public curSollProfL: any;
  public curSollProfR: any;

  public curSollProfL_org: any;
  public curSollProfR_org: any;

  public curIstProfL: any;
  public curIstProfR: any;
  public showImportModal: boolean = false;
  public moveStepL: number = 1;
  public moveStepR: number = 1;

  public xy = { x: 0.0, y: 0.0, x2: 0.0, y2: 0.0 };

  modalImportGroup = new UntypedFormGroup({
    sollprofil: new UntypedFormControl(''),
    spurweite: new UntypedFormControl(1435),
    messdate: new UntypedFormControl(''),
    neigung: new UntypedFormControl('0'),
  });

  _selected: any = [];
  set selected(newSelect: any) {
    this._selected = newSelect;
  }

  get selected() {
    return this._selected;
  }

  ori: 'h' | 'v' = 'h';
  graphL = {
    show: false,
    eid: 0,
    id: null as PlotlyHTMLElement,
    layout: {
      autosize: true,
      showlegend: true,
      legend: { orientation: this.ori },
      width: 700,
      height: 650,
      margin: {
        l: 35,
        r: 20,
        b: 25,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        //autorange: true,
        range: [-40, 100], //[Math.min(...measurements) - 10, Math.max(...measurements) + 10],
        //type: "linear",
      },
      xaxis: {
        //autorange: true,
        range: [-90, 50],
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d'],
    },
    data: [],
  };

  graphR = {
    id: null as PlotlyHTMLElement,
    layout: {
      autosize: true,
      showlegend: true,
      legend: { orientation: this.ori },
      width: 700,
      height: 650,
      margin: {
        l: 35,
        r: 20,
        b: 25,
        t: 20,
        pad: 0,
      },
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      yaxis: {
        //autorange: true,
        range: [-40, 100], //[Math.min(...measurements) - 10, Math.max(...measurements) + 10],
        //type: "linear",
      },
      xaxis: {
        //autorange: true,
        range: [-50, 90],
      },
    },
    config: {
      responsive: true,
      displaylogo: false,
      locale: 'de',
      modeBarButtonsToRemove: ['sendDataToCloud', 'lasso2d'],
    },
    data: [],
  };

  readonly accept = input('application/vnd.ms-excel,.txt,.brml,.rml,.csv,.ban');
  private leftBoundL: number = undefined;
  private leftBoundR: number = undefined;
  public indexScrub: BehaviorSubject<number> = new BehaviorSubject(0);
  public closedialog: BehaviorSubject<boolean> = new BehaviorSubject(false);
  name: string = '';

  set data(dataIn: any) {
    this.stammid = dataIn.id;
    this.name = dataIn.name;
    this.loadRM();
  }

  getRotationData(angle: number, SollProf: any, direction: 'L' | 'R', name: string) {
    //const rotated = direction === 'L' ? this.rotateClockwise( SollProf.x, SollProf.y, angle) : this.rotateClockwise( SollProf.x, SollProf.y, -angle);
    const rotated = this.rotateClockwise(SollProf.x, SollProf.y, angle);
    const rotatedX = rotated.map(p => Number(p.x.toFixed(4)));
    const rotatedY = rotated.map(p => Number(p.y.toFixed(4)));
    return { x: rotatedX, y: rotatedY, name: name };
  }

  onNeigungChanged(event: Event) {
    const value = (event.target as HTMLSelectElement).value;

    this.graphL.show = false;

    switch (value) {
      case '0':
        this.graphL.data[0] = this.curSollProfL_org;
        this.graphR.data[0] = this.curSollProfR_org;
        break;

      case '20':
        const ratio20 = 1 / 20;
        const angleDeg20 = Math.atan(ratio20) * (180 / Math.PI);
        this.graphL.data[0] = this.getRotationData(angleDeg20, this.curSollProfL_org, 'L', this.graphL.data[0].name);
        this.graphR.data[0] = this.getRotationData(-angleDeg20, this.curSollProfR_org, 'R', this.graphR.data[0].name);

        break;

      case '40':
        const ratio40 = 1 / 40;
        const angleDeg40 = Math.atan(ratio40) * (180 / Math.PI);
        this.graphL.data[0] = this.getRotationData(angleDeg40, this.curSollProfL_org, 'L', this.graphL.data[0].name);
        this.graphR.data[0] = this.getRotationData(-angleDeg40, this.curSollProfR_org, 'R', this.graphR.data[0].name);
        break;
    }

    setTimeout(() => {
      // FORCE REFRESH
      this.graphL = {
        ...this.graphL,
        data: [...this.graphL.data],
      };

      this.graphR = {
        ...this.graphR,
        data: [...this.graphR.data],
      };

      this.graphL.show = true;
    });

    console.log(value);
  }

  rotateClockwise(x: number[], y: number[], angleDeg: number): Point[] {
    const angleRad = (angleDeg * Math.PI) / 180;

    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    return x.map((xVal, i) => {
      const yVal = y[i];

      return {
        x: xVal * cos + yVal * sin,
        y: -xVal * sin + yVal * cos,
      };
    });
  }

  getPlotID(myPlot: PlotlyHTMLElement, side: 'l' | 'r') {
    switch (side) {
      case 'l':
        this.graphL.id = myPlot;
        break;
      case 'r':
        this.graphR.id = myPlot;
        break;
    }
    myPlot.on('plotly_click', (data: PlotMouseEvent) => {
      let graph: any;
      side == 'l' ? (graph = this.graphL) : (graph = this.graphR);

      graph.data = graph.data.filter(item => item.name != 'Distanzmessung');
      if (data.points.length == 1) {
        this.graphL.show = false;

        if (!this.xy.x || (this.xy.x && this.xy.x2)) {
          this.xy.x = data.points[0].x as number;
          this.xy.y = data.points[0].y as number;
          this.xy.x2 = undefined;
          this.xy.y2 = undefined;
          var dataPoint = {
            x: [this.xy.x],
            y: [this.xy.y],
            name: 'Distanzmessung',
            type: 'lines+marker',
          };
          var txt = 'x=0.00 mm y=0.00 mm dist=0.00 mm';
          //Plotly.addTraces(graph.id,{ x: [this.xy.x], y: [this.xy.y], name: "Distanzmessung"})
        } else {
          this.xy.x2 = data.points[0].x as number;
          this.xy.y2 = data.points[0].y as number;
          let dist = this.getDistanceNum(this.xy.x, this.xy.x2, this.xy.y, this.xy.y2);
          var txt =
            'x=' +
            (this.xy.x - this.xy.x2).toFixed(2) +
            ' mm y=' +
            (this.xy.y - this.xy.y2).toFixed(2) +
            ' mm dist=' +
            dist +
            ' mm';
          var dataPoint = {
            x: [this.xy.x, this.xy.x2],
            y: [this.xy.y, this.xy.y2],
            name: 'Distanzmessung',
            type: 'lines+marker',
          };
          //Plotly.addTraces(graph.id,{ x: [this.xy.x, this.xy.x2], y: [this.xy.y, this.xy.y2], name: "Distanzmessung"})
        }

        graph.layout.annotations = [
          {
            text: txt,
            x: NaN, //Garantiert dass Annotation immer oben links zu sehen ist.
            y: NaN,
            showarrow: false,
            font: {
              family: 'Arial, sans-serif',
              size: 16,
              color: '#ff0000',
            },
          },
        ];

        graph.data.push(dataPoint);
        // Plotly.react(this.graphL.id,this.graphL.data, this.graphL.layout);
        // Plotly.react(this.graphR.id,this.graphR.data, this.graphR.layout);
        setTimeout(() => {
          this.graphL.show = true;
        });
      }
    });
  }
  public getDistanceNum(p1x: number, p2x: number, p1y: number, p2y: number) {
    let a = p1x - p2x;
    let b = p1y - p2y;

    return Math.sqrt(a * a + b * b).toFixed(2);
  }

  doMove(val, side, dir) {
    let graph: any;
    side == 'L' ? (graph = this.graphL) : (graph = this.graphR);
    if (dir == 'X') {
      graph.data[1].x = graph.data[1].x.map(xVal => xVal + val);
    } else {
      graph.data[1].y = graph.data[1].y.map(xVal => xVal - val);
    }
    redraw(graph.id);
  }

  setMoveStep(i: any, mode: string) {
    let val = +i.currentTarget.value;
    if (mode == 'L') this.moveStepL = val;
    else this.moveStepR = val;
  }

  public loadRM() {
    this.apiservice
      .getRailmonitor(this.stammid)
      .pipe(first())
      .subscribe((res: any) => {
        if (res) {
          if (res && res.rows?.length > 0) {
            this.profiles = res.rows.map(pro => {
              if (!this.leftBoundL || !this.leftBoundR) {
                this.leftBoundL = pro.SollProfil.SchHMax * 2; //this.profiles[0].ProfilLinks[0][0];
                this.leftBoundR = this.leftBoundL;
              }
              return pro;
            });
          }
        }
      });
  }

  selectionChanged(event) {
    if (event) {
      this.graphL.show = false;
      this.graphL.data = [];
      this.graphR.data = [];
      let i = 0;
      event.forEach(element => {
        if (i == 0) {
          switch (element.Neigung.toString()) {
            case '0':
              this.graphL.data.push(element.SollProfil.ProfilL);
              this.graphR.data.push(element.SollProfil.ProfilR);
              break;

            case '20':
            { 
              const ratio20 = 1 / 20;
              const angleDeg20 = Math.atan(ratio20) * (180 / Math.PI);
              this.graphL.data.push(
                this.getRotationData(angleDeg20, element.SollProfil.ProfilL, 'L', element.SollProfil.Bezeichnung)
              );
              this.graphR.data.push(
                this.getRotationData(-angleDeg20, element.SollProfil.ProfilR, 'R', element.SollProfil.Bezeichnung)
              );

              break; }

            case '40':
            { 
              const ratio40 = 1 / 40;
              const angleDeg40 = Math.atan(ratio40) * (180 / Math.PI);
              this.graphL.data.push(
                this.getRotationData(angleDeg40, element.SollProfil.ProfilL, 'L', element.SollProfil.Bezeichnung)
              );
              this.graphR.data.push(
                this.getRotationData(-angleDeg40, element.SollProfil.ProfilR, 'R', element.SollProfil.Bezeichnung)
              );
              break; }
          }
        }

        if (element.SollProfil.Typ == 'Vignol') {
          this.graphR.layout.xaxis = { range: [-70, 70] };
          this.graphR.layout.yaxis = { range: [-70, 70] };
          this.graphL.layout.xaxis = { range: [-70, 70] };
          this.graphL.layout.yaxis = { range: [-70, 70] };
        } else {
          this.graphR.layout.xaxis = { range: [-50, 90] };
          this.graphR.layout.yaxis = { range: [-40, 100] };
          this.graphL.layout.xaxis = { range: [-90, 50] };
          this.graphL.layout.yaxis = { range: [-40, 100] };
        }

        //this.graphL.data.push(element.SollProfil.ProfilL);
        //this.graphR.data.push(element.SollProfil.ProfilR);
        this.graphL.data.push(element.ProfilLinks);
        this.graphR.data.push(element.ProfilRechts);
        this.graphL.eid = element.ID;
        i++;
      });

      // Plotly.react(this.graphL.id,this.graphL.data, this.graphL.layout);
      // Plotly.react(this.graphR.id,this.graphR.data, this.graphR.layout);
      setTimeout(() => {
        this.graphL.show = true;
      });
    }
  }

  setImportModal() {
    this.apiservice
      .getRailmonitorSollprofile()
      .pipe()
      .subscribe((v: any) => {
        this.showImportModal = true;
        this.selected = [];
        this.graphL.show = false;
        this.graphL.data = [];
        this.graphR.data = [];
        this.sollProfile = v;
        setTimeout(() => {
          this.graphL.show = true;
        }, 100);
      });
  }

  closeImportModal() {
    this.showImportModal = false;
    this.graphL.show = false;
    this.graphL.data = [];
    this.graphR.data = [];
    setTimeout(() => {
      this.graphL.show = true;
    });
  }

  loadSoll(event: any) {
    this.graphL.show = false;
    this.curSoll = this.sollProfile[event.target.value].ID;
    this.curSollProfL = this.sollProfile[event.target.value].ProfilL;
    this.curSollProfR = this.sollProfile[event.target.value].ProfilR;

    this.curSollProfL_org = this.sollProfile[event.target.value].ProfilL;
    this.curSollProfR_org = this.sollProfile[event.target.value].ProfilR;

    this.graphL.data = [this.curSollProfL];
    if (this.curIstProfL) this.graphL.data.push(this.curIstProfL);
    this.graphR.data = [this.curSollProfR];
    if (this.curIstProfR) this.graphR.data.push(this.curIstProfR);

    if (this.sollProfile[event.target.value].Typ == 'Vignol') {
      this.graphR.layout.xaxis = { range: [-70, 70] };
      this.graphR.layout.yaxis = { range: [-70, 70] };
      this.graphL.layout.xaxis = { range: [-70, 70] };
      this.graphL.layout.yaxis = { range: [-70, 70] };
    } else {
      this.graphR.layout.xaxis = { range: [-90, 50] };
      this.graphR.layout.yaxis = { range: [-40, 100] };
      this.graphL.layout.xaxis = { range: [-90, 50] };
      this.graphL.layout.yaxis = { range: [-40, 100] };
    }

    setTimeout(() => {
      this.graphL.show = true;
    });
    // Plotly.react(this.graphL.id,this.graphL.data, this.graphL.layout);
    // Plotly.react(this.graphR.id,this.graphR.data, this.graphR.layout);
  }

  importRailmonitor() {
    const fileUpload = document.getElementById('fileUpload') as HTMLInputElement;
    fileUpload.value = '';
    fileUpload.click();
  }
  importRailmonitorRechts() {
    const fileUpload2 = document.getElementById('fileUpload2') as HTMLInputElement;
    fileUpload2.value = '';
    fileUpload2.click();
  }
  startImport(dir: string) {
    const files =
      dir == 'links' ?
        (document.getElementById('fileUpload') as HTMLInputElement)
      : (document.getElementById('fileUpload2') as HTMLInputElement);

    let fileext = files.files[0].name.substring(files.files[0].name.indexOf('.'));
    if (fileext.toLowerCase() == '.rml') {
      for (const element of files.files) {
        const f = element;
        this.internStartImport(f, dir);
      }
    } else {
      this.internStartImport(files.files[0], dir)
    }
    
  }
  internStartImport(files: any, dir: string) {
    let fileext = files.name.substring(files.name.indexOf('.'));
    const formData = new FormData();
    formData.append('file', files);
    if (fileext.toLowerCase() == '.xls') formData.append('retFile', 'true');
    this.apiservice
      .uploadFiles(formData)
      .pipe()
      .subscribe((v: any) => {
        if (v.type == 4) {
          let f: any = {};
          let file = v.body.url;
          let fileContent = v.body.fileContent;
          let data = '';

          f.files = file;
          f.Direction = dir;
          if (fileext.toLowerCase() == '.xls') {
            //const wb = new Excel.Workbook();

            let fileXL = Buffer.from(fileContent, 'base64');

            const wb: WorkBook = read(fileXL);
            const ws: WorkSheet = wb.Sheets[wb.SheetNames[0]];
            for (let i = 2; i < 3000; i++) {
              if (ws['E' + i.toString()] == undefined) break;
              data += ws['D' + i.toString()].v + ';' + ws['E' + i.toString()].v + '|';
            }
            f.Data = data;
            this.graphL.show = false;
            this.apiservice
              .getRailmonitorImport(f)
              .pipe()
              .subscribe((val: any) => {
                if (val) {
                  if (dir == 'links') {
                    this.graphL.data.push(val.dtL);
                  } else {
                    this.graphR.data.push(val.dtR);
                  }
                  this.graphL.show = true;
                }
              });
          }
          else {
            this.graphL.show = false;
            this.apiservice
              .getRailmonitorImport(f)
              .pipe()
              .subscribe((val: any) => {
                if (val) {
                  if (dir == 'links') {
                    this.graphL.data = [this.curSollProfL, val.dtL];
                  } else {
                    this.graphR.data = [this.curSollProfR, val.dtR];
                  }
                  this.graphL.show = true;
                }
              });
          }
        }
      });
  }

  saveImport() {
    let test = true;

    if (!this.modalImportGroup.controls.messdate.value) {
      this.toastr.warning(this.mrTranslate.transform('Bitte das Messdatum angeben.'));
      test = false;
    }
    if (!this.modalImportGroup.controls.spurweite.value) {
      this.toastr.warning(this.mrTranslate.transform('Bitte die Spurweite angeben.'));
      test = false;
    }
    if (!this.graphL.data[1] || !this.graphR.data[1]) {
      this.toastr.warning(this.mrTranslate.transform('Es muss für beide Seiten ein Import durchgeführt werden!'));
      test = false;
    }

    if (test) {
      let exp: any = {};
      let dataL = '';
      let dataR = '';
      exp.curSollID = this.curSoll;
      exp.messdate = this.modalImportGroup.controls.messdate.value;
      exp.spurweite = this.modalImportGroup.controls.spurweite.value;
      exp.neigung = this.modalImportGroup.controls.neigung.value;
      for (let i = 0; i < this.graphL.data[1].x.length; i++) {
        dataL = dataL + this.graphL.data[1].x[i].toFixed(4) + ';' + this.graphL.data[1].y[i].toFixed(4) + '\r\n';
      }
      dataL = dataL.substring(0, dataL.length - 2);
      for (let i = 0; i < this.graphR.data[1].x.length; i++) {
        dataR = dataR + this.graphR.data[1].x[i].toFixed(4) + ';' + this.graphR.data[1].y[i].toFixed(4) + '\r\n';
      }
      dataR = dataR.substring(0, dataR.length - 2);
      exp.dataL = dataL;
      exp.dataR = dataR;
      this.apiservice
        .saveRailmonitorImport(this.stammid, exp)
        .pipe()
        .subscribe((val: any) => {
          this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
          this.loadRM();
          this.closeImportModal();
          this.modalImportGroup.reset();
        });
    }
  }

  async deleteRailMonitor(id: any) {
    const text = this.mrTranslate.transform('Daten wirklich löschen?');
    const ok = await this.confirmService.danger(text);
    if (ok && id) {
      this.apiservice
        .deleteRailmonitor(id)
        .pipe()
        .subscribe((val: any) => {
          this.toastr.success(this.mrTranslate.transform('Daten gelöscht'));
          this.loadRM();
        });
    }
  }
  ngOnDestroy(): void {
    this.graphL = undefined;
    this.graphR = undefined;
  }

  close() {
    this.closedialog.next(true);
    this.indexScrub.next(-1);
  }

  

  setSmooth() {
    const l = this.graphL.data[1];
    const r = this.graphR.data[1];
    if (!l || !r) return;
    this.graphL.show = false;

    const pointsL = l.x.map((x, i) => ({ x, y: l.y[i] }));
    const pointsR = r.x.map((x, i) => ({ x, y: r.y[i] }));

    const smoothedL = LOWESS.smooth(pointsL, {
      span: 0.02,
      robustIterations: 2,
    });
    const smoothedR = LOWESS.smooth(pointsR, {
      span: 0.02,
      robustIterations: 2,
    });

    // Ersetze Trace-Objekte komplett
    this.graphL.data[1] = {
      ...l,
      x: smoothedL.map(p => p.x),
      y: smoothedL.map(p => p.y),
    };
    this.graphR.data[1] = {
      ...r,
      x: smoothedR.map(p => p.x),
      y: smoothedR.map(p => p.y),
    };

    this.profiles.forEach(element => {
      if (element.ID == this.graphL.eid) {
        element.ProfilLinks = this.graphL.data[1];
        element.ProfilRechts = this.graphR.data[1];
      }
    });

    // **Sicherstellen, dass beide Plots sichtbar sind**
    requestAnimationFrame(() => {
      this.graphL.show = true;
    });
  }

  getDimensioning() {
    this.toastr.info(
      this.mrTranslate.transform('Diese Funktion ist zur Zeit in Arbeit'),
      this.mrTranslate.transform('MR.pro'),
      {
        positionClass: 'toast-center-screen',
      }
    );
  }
}
